import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMainAxios } from '../utils/util';
import type { AuthUser, AuthResponse, AuthMeResponse, KisApiMst } from '../types';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  portfolios: KisApiMst[];
  selectedPfId: number | null;
  /** 선택한 계좌에 주문할 수 있나 (서버 PfAccess 가 준 값). false 면 주문 버튼을 숨긴다 — 실제 차단은 서버가 한다 */
  canOrder: boolean;
  /** Dev — 계좌를 받지 않고 KIS 를 직접 치는 기능(체결 취소·정정 등)은 Dev 만 */
  isDev: boolean;
  loading: boolean;
  login: (email: string, password: string, tradePwd?: string) => Promise<string | null>;
  register: (email: string, password: string, name: string) => Promise<string | null>;
  logout: () => void;
  selectPortfolio: (pfId: number) => void;
  refreshPortfolios: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 계좌 목록을 화면에 보여줄 순서로 정렬한다.
 *
 * 서버가 정렬해 주지 않으므로(kisapimst 조회에 OrderBy 없음) 배열 순서는
 * DB 가 돌려주는 대로다. 그대로 두면 sim(가상체결) 계좌가 목록 맨 앞에 오고,
 * 기본 선택도 sim 이 되는 일이 생긴다.
 *
 * 순서:
 *   1) 실제 브로커 계좌(kis·kiwoom — 실전/모의 모두) 먼저, sim(가상체결)은 뒤
 *   2) 주식 먼저, 선물옵션 뒤 — 포트폴리오 화면은 주식 기준이다
 *   3) **시작일 최신순** — 모의 대회는 3개월마다 새 계좌가 생긴다. id 순이면 끝난 대회 계좌가
 *      기본이 됐다 (2026-11-03: 6 모의주식-2608 이 닫힌 뒤에도 기본으로 나왔다)
 *   4) 같으면 id 내림차순 — 순서를 고정해 화면이 매번 달라지지 않게
 *
 * 드롭다운 표시 순서와 기본 선택이 이 하나로 함께 결정된다
 * (기본 선택 = 정렬된 목록의 첫 번째).
 */
const sortPortfolios = (list: KisApiMst[]): KisApiMst[] =>
  [...list].sort((a, b) => {
    const aSim = a.broker === 'sim' ? 1 : 0;
    const bSim = b.broker === 'sim' ? 1 : 0;
    if (aSim !== bSim) return aSim - bSim;   // sim 을 뒤로
    const aFut = a.acntType === 'fut' ? 1 : 0;
    const bFut = b.acntType === 'fut' ? 1 : 0;
    if (aFut !== bFut) return aFut - bFut;   // 선물옵션을 뒤로
    if ((a.initD ?? '') !== (b.initD ?? '')) return (b.initD ?? '').localeCompare(a.initD ?? '');
    return b.id - a.id;
  });

/**
 * 일반 화면에 보일 계좌 — 모의/실전 모드가 맞고, 사용중이고, **기간(endD)이 지나지 않은** 것만.
 * 정지·만료 계좌의 과거 데이터는 '포트폴리오복기' 메뉴에서 본다.
 */
const visiblePortfolios = (list: KisApiMst[]): KisApiMst[] => {
  const isReal = localStorage.getItem('fds_isReal') === 'true';
  const today = new Date().toLocaleDateString('sv-SE');
  return sortPortfolios(list.filter(p => p.isReal === isReal && p.isActive !== false && !(p.endD && p.endD < today)));
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [portfolios, setPortfolios] = useState<KisApiMst[]>([]);
  const [selectedPfId, setSelectedPfId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const handleAuthSuccess = useCallback((data: AuthResponse) => {
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('fds_token', data.token);

    const sorted = visiblePortfolios(data.portfolios);
    setPortfolios(sorted);

    if (sorted.length > 0) {
      const savedPfId = localStorage.getItem('fds_selectedPfId');
      const found = savedPfId ? sorted.find(p => p.id === Number(savedPfId)) : null;
      const pfId = found ? found.id : sorted[0].id;
      setSelectedPfId(pfId);
      localStorage.setItem('fds_selectedPfId', String(pfId));
    } else {
      setSelectedPfId(null);
    }
  }, []);

  // 앱 시작 시 토큰으로 사용자 복원
  useEffect(() => {
    const savedToken = localStorage.getItem('fds_token');
    if (!savedToken) {
      setLoading(false);
      return;
    }
    setToken(savedToken);

    const axios = getMainAxios();
    axios.get<AuthMeResponse>('/api/v1/auth/me')
      .then(res => {
        const data = res.data;
        setUser(data.user);
        const sorted = visiblePortfolios(data.portfolios);
        setPortfolios(sorted);

        if (sorted.length > 0) {
          const savedPfId = localStorage.getItem('fds_selectedPfId');
          const found = savedPfId ? sorted.find(p => p.id === Number(savedPfId)) : null;
          const pfId = found ? found.id : sorted[0].id;
          setSelectedPfId(pfId);
          localStorage.setItem('fds_selectedPfId', String(pfId));
        }
      })
      .catch(() => {
        // 토큰 만료 등 → 로그아웃
        localStorage.removeItem('fds_token');
        localStorage.removeItem('fds_selectedPfId');
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string, tradePwd?: string): Promise<string | null> => {
    try {
      const axios = getMainAxios();
      const body: any = { email, password };
      if (tradePwd) body.tradePwd = tradePwd;
      const res = await axios.post<AuthResponse>('/api/v1/auth/login', body);
      handleAuthSuccess(res.data);
      return null;
    } catch (err: any) {
      return err.response?.data?.error || '로그인에 실패했습니다.';
    }
  }, [handleAuthSuccess]);

  const register = useCallback(async (email: string, password: string, name: string): Promise<string | null> => {
    try {
      const axios = getMainAxios();
      const res = await axios.post<AuthResponse>('/api/v1/auth/register', { email, password, name });
      handleAuthSuccess(res.data);
      return null;
    } catch (err: any) {
      return err.response?.data?.error || '회원가입에 실패했습니다.';
    }
  }, [handleAuthSuccess]);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setPortfolios([]);
    setSelectedPfId(null);
    localStorage.removeItem('fds_token');
    localStorage.removeItem('fds_selectedPfId');
    localStorage.removeItem('fds_currentPage');
  }, []);

  const selectPortfolio = useCallback((pfId: number) => {
    setSelectedPfId(pfId);
    localStorage.setItem('fds_selectedPfId', String(pfId));
  }, []);

  const refreshPortfolios = useCallback(async () => {
    try {
      const axios = getMainAxios();
      const res = await axios.get<AuthMeResponse>('/api/v1/auth/me');
      const data = res.data;
      // 로그인/복원과 동일한 필터 (모의·실전 + 사용중 + 기간 안)
      const sorted = visiblePortfolios(data.portfolios);
      setPortfolios(sorted);
      if (sorted.length > 0 && !selectedPfId) {
        const pfId = sorted[0].id;
        setSelectedPfId(pfId);
        localStorage.setItem('fds_selectedPfId', String(pfId));
      }
    } catch {
      // ignore
    }
  }, [selectedPfId]);

  // 서버가 canOrder 를 안 주던 구버전 응답이면 true 로 본다 (서버가 어차피 막는다)
  const canOrder = portfolios.find(p => p.id === selectedPfId)?.canOrder !== false && selectedPfId != null;
  const isDev = (user?.role ?? 0) >= 90;

  return (
    <AuthContext.Provider value={{
      user, token, portfolios, selectedPfId, canOrder, isDev, loading,
      login, register, logout, selectPortfolio, refreshPortfolios,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
